<template>
  <div>
    <!-- Title -->
    <list-header :title="$t('categories')" hide-show-categories-button>
      <template v-if="showHideButton" #action>
        <sf-icon-button
          v-if="showCategories"
          color=""
          :icon="mdiArrowCollapseLeft"
          :aria-label="$t('hideCategories')"
          @click="useCategories().setShowCategories(false)"
        />
      </template>
      <template v-else-if="showCloseButton" #action>
        <sf-icon-button color="" :icon="mdiClose" @click="$emit('close')" />
      </template>
    </list-header>
    <!-- Loading -->
    <div v-if="loading" class="mt-2">
      <v-skeleton-loader v-for="index in 10" :key="index" class="mt-2" type="text" />
    </div>
    <!-- Categories -->
    <list-content
      v-else-if="categories.length > 0"
      class="d-flex flex-column overflow-y-auto scrollbar pr-1"
    >
      <v-treeview
        id="category-tree"
        activatable
        active-strategy="single-independent"
        :activated="selectedCategory !== undefined ? [selectedCategory] : []"
        density="compact"
        color="primary"
        :items="categories"
        item-title="name"
        item-value="searchTerm"
        indent-lines="default"
        return-object
        :opened="openCategories"
        separate-roots
        :bg-color="$vuetify.display.mobile ? '' : 'background'"
        @update:activated="onSelect($event as TreeViewCategory[])"
      >
      </v-treeview>
    </list-content>
    <!-- Empty -->
    <list-empty
      v-else-if="categories.length === 0"
      :icon="mdiStore"
      :message="$t('noCategoriesFound')"
    />
  </div>
</template>

<script setup lang="ts">
import SfIconButton from '@/components/button/SfIconButton.vue'
import ListContent from '@/components/list/ListContent.vue'
import ListEmpty from '@/components/list/ListEmpty.vue'
import ListHeader from '@/components/list/ListHeader.vue'
import useCategories from '@/modules/categories/useCategories'
import CategoryEvent from '@/modules/tracking/events/category'
import { trackEvent } from '@/modules/tracking/useTracking'
import { TreeViewCategory } from '@/types'
import { mdiArrowCollapseLeft, mdiClose, mdiStore } from '@mdi/js'
import { useQuery } from '@pinia/colada'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'
import { categoriesQueryOptions } from '@/modules/categories/useCategoriesColada'

defineProps({
  showHideButton: {
    type: Boolean,
    default: false
  },
  showCloseButton: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  update: [string]
  close: []
}>()

const { openCategories, selectedCategory, showCategories } = storeToRefs(useCategories())

const { data: categoriesData, asyncStatus } = useQuery(categoriesQueryOptions())

const loading = computed<boolean>(() => asyncStatus.value === 'loading')
const categories = computed<TreeViewCategory[]>(() => {
  return categoriesData.value ?? []
})

watch(selectedCategory, (newValue) => {
  if (newValue) {
    trackEvent(CategoryEvent.PageView(newValue.name))
  }
})

const onSelect = (selected: TreeViewCategory[]) => {
  if (selected[0]) {
    if (selected[0].isSelectable) {
      emit('update', selected[0].searchTerm)
    } else {
      openCategories.value.push(selected[0])
    }
  }
}
</script>

<style scoped>
#category-tree :deep(.v-list-item-title) {
  text-wrap: wrap;
}
</style>
